import React, { useState, useEffect } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { Swiper, SwiperSlide } from 'swiper/react'
import type { Swiper as SwiperClass } from 'swiper'
import 'swiper/css'
import * as S from './SectionDepoimentos.styles'

export interface Testimonial {
  text: string
  author: string
  role: string
}

interface SectionDepoimentosProps {
  title?: string
  testimonials?: Testimonial[]
  showAll?: boolean
  fontSize?: string
}

const SectionDepoimentos = ({
  title: titleProp,
  testimonials: testimonialsProp,
  showAll = false,
  fontSize
}: SectionDepoimentosProps = {}) => {
  const { t } = useTranslation()
  const [current, setCurrent] = useState(0)
  const [isMobile, setIsMobile] = useState(false)
  const [swiper, setSwiper] = useState<SwiperClass | null>(null)
  const visible = 3

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.matchMedia('(max-width: 900px)').matches)
    }
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  const translatedTitle = t('landingV2.sectionDepoimentos.title')
  const resolvedTitle = titleProp ?? translatedTitle

  const translatedTestimonials = t(
    'landingV2.sectionDepoimentos.testimonials',
    {
      returnObjects: true
    }
  ) as Testimonial[]

  const resolvedTestimonials: Testimonial[] =
    testimonialsProp ??
    (Array.isArray(translatedTestimonials) ? translatedTestimonials : [])

  const total = resolvedTestimonials.length
  const visibleCount = isMobile ? 1.3 : showAll ? total : visible
  const maxIndex = isMobile
    ? Math.max(0, total - 1)
    : showAll
      ? 0
      : Math.max(0, total - visible)

  useEffect(() => {
    if (current > maxIndex) {
      setCurrent(maxIndex)
      if (swiper) {
        swiper.slideTo(maxIndex)
      }
    }
  }, [visibleCount, maxIndex, current, swiper])

  const handleSlideChange = (swiperInst: SwiperClass) => {
    setCurrent(swiperInst.activeIndex)
  }

  const handleBulletClick = (idx: number) => {
    setCurrent(idx)
    if (swiper) {
      swiper.slideTo(idx)
    }
  }

  return (
    <S.Section>
      <S.Title>{resolvedTitle}</S.Title>
      <S.CarouselWrapper>
        <S.StyledSwiper>
          <Swiper
            onSwiper={setSwiper}
            onSlideChange={handleSlideChange}
            slidesPerView={1.3}
            spaceBetween={16}
            breakpoints={{
              900: {
                slidesPerView: showAll ? total : visible,
                spaceBetween: 32,
                allowTouchMove: false
              }
            }}
          >
            {resolvedTestimonials.map((item, idx) => (
              <SwiperSlide key={idx}>
                <S.Card>
                  <S.Text1 style={fontSize ? { fontSize } : undefined}>
                    {item.text}
                  </S.Text1>
                  <S.Text2 style={fontSize ? { fontSize } : undefined}>
                    <strong>{item.author}</strong>
                    <br />
                    {testimonialsProp ? (
                      <span dangerouslySetInnerHTML={{ __html: item.role }} />
                    ) : (
                      <Trans
                        i18nKey={`landingV2.sectionDepoimentos.testimonials.${idx}.role`}
                        components={{ br: <br /> }}
                      >
                        {item.role}
                      </Trans>
                    )}
                  </S.Text2>
                </S.Card>
              </SwiperSlide>
            ))}
          </Swiper>
        </S.StyledSwiper>
        {total > (isMobile ? 1 : visibleCount) && (
          <S.Bullets>
            {Array.from({ length: maxIndex + 1 }).map((_, idx) => (
              <S.Bullet
                key={idx}
                active={idx === current}
                onClick={() => handleBulletClick(idx)}
                aria-label={`Ir para depoimento ${idx + 1}`}
              />
            ))}
          </S.Bullets>
        )}
      </S.CarouselWrapper>
    </S.Section>
  )
}

export default SectionDepoimentos
